import React, { useEffect, useState } from 'react';
import { FormattedMessage, FormattedDate } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { FloatTextArea } from 'ui-components/components/float-text-area';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Modal } from 'ui-components/components/modal';
import { useModalContext } from 'ui-components/components/modal/context';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { NotificationService } from 'ui-components/services/notification-service';

import CancelButton from './cancel-button';
import CancellationReasonDropdown from './cancel-reason-dropdown';
import FollowUpDropdown from './follow-up-dropdown';
import { createLookupHash } from '../../../../../../services/hash-service';
import { getCancellationInfo, getFreeStampsSellerProviders } from '../../../../api/subscription';
import { DEFAULT_CANCEL_REASON_ID, OTHER_CANCEL_REASON_ID } from '../../../constants';
import { getCancellationInfoErrorNotification } from '../../../constants/notifications';
import { usePaymentAndSubscriptionPageContext } from '../../../context/payment-and-subscription-page-context';
import { messages } from '../../../messages';
import style from '../../../style.module.less';
import threePLMessages from '../../3pl-provider-bundle/messages';

const MINIMUM_FEEDBACK_LENGTH = 10;

const CancelAccountContent = () => {
  const {
    followUpExplain,
    cancelReasonId,
    comments,
    setComments,
    cancellationInfo,
    setCancellationInfo,
    setSellerStampsProviders,
    setIsCancellationModalOpen,
    is3plPlanEnabled,
  } = usePaymentAndSubscriptionPageContext();

  const [isCancellationModalLoading, setIsCancellationModalLoading] = useState(true);

  useEffect(() => {
    getCancellationInfo()
      .then((response) => {
        setCancellationInfo(response?.data ?? {});
      })
      .catch(() => {
        NotificationService.showNotification(getCancellationInfoErrorNotification);
      })
      .finally(() => {
        setIsCancellationModalLoading(false);
      });

    getFreeStampsSellerProviders().then((response) => {
      setSellerStampsProviders(response?.data);
    });
  }, [setCancellationInfo, setSellerStampsProviders, setIsCancellationModalLoading, setIsCancellationModalOpen]);

  const { endDateTimeUtc = '', cancelReasons = [] } = cancellationInfo;
  const cancelReasonData = createLookupHash(cancelReasons, 'cancelReasonId');
  const cancellationEffectiveDate = <FormattedDate day='2-digit' month='long' value={endDateTimeUtc} weekday='long' year='numeric' />;
  const { onClose } = useModalContext();

  const shouldCTAButtonBeDisabled = () => {
    const defaultReasonSelected = cancelReasonId === DEFAULT_CANCEL_REASON_ID;
    const otherReasonSelected = cancelReasonId === OTHER_CANCEL_REASON_ID;
    const minimumOtherFeedbackMet = followUpExplain?.length >= MINIMUM_FEEDBACK_LENGTH || comments?.length >= MINIMUM_FEEDBACK_LENGTH;

    return defaultReasonSelected || (otherReasonSelected && !minimumOtherFeedbackMet);
  };

  const userHasSelectedACancelReason = parseInt(cancelReasonId, 10) !== 0;

  return (
    <Modal className={style['modal-base']} size='medium'>
      <ModalHeader closeModal={onClose}>
        <FormattedMessage {...messages.cancelSubscriptionModalHeader} />
      </ModalHeader>
      <ModalBody className={style['cancellation-modal-body']}>
        {isCancellationModalLoading ? (
          <div className={style['loading-indicator-container']}>
            <LoadingIndicator className={style['loading-indicator']} />
          </div>
        ) : (
          <>
            {is3plPlanEnabled ? (
              <div className={style['cancellation-modal-description']}>
                <FormattedMessage {...threePLMessages.willRemove3PLBundleImmediately} />
              </div>
            ) : null}
            <div className={style['cancellation-modal-description']}>
              <FormattedMessage
                {...messages.cancelledSubscriptionWillContinueUntilAndOneBalanceRefundDisclaimer}
                values={{
                  b: (chunks) => <b>{chunks}</b>,
                  cancellationEffectiveDate,
                }}
              />
            </div>
            <div className={style['cancellation-modal-feedback']}>
              <FormattedMessage {...messages.feedbackMatters} />
            </div>
            <CancellationReasonDropdown cancelReasonData={cancelReasonData} />
            {userHasSelectedACancelReason ? <FollowUpDropdown cancelReasonData={cancelReasonData} /> : null}
            <div className={style['cancellation-label']}>
              <FormattedMessage {...messages.cancellationComments} />
            </div>
            <FloatTextArea onChange={setComments} value={comments} />
          </>
        )}
      </ModalBody>
      <ModalFooter className={style.footer}>
        <Button onClick={onClose} theme={THEME_CONSTANTS.tertiary}>
          <FormattedMessage {...messages.cancel} />
        </Button>
        <CancelButton cancelReasonData={cancelReasonData} disabled={shouldCTAButtonBeDisabled()} />
      </ModalFooter>
    </Modal>
  );
};

export default CancelAccountContent;
